import type { ArtifactActivity } from './api';

export type ArtifactActivityIcon =
  | 'circle'
  | 'check'
  | 'archive'
  | 'edit'
  | 'user'
  | 'shield'
  | 'link'
  | 'trash';

export interface FormattedActivity {
  icon: ArtifactActivityIcon;
  label: string;
  detailText?: string;
}

function stringDetail(details: Record<string, unknown>, key: string): string | null {
  const value = details[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function transition(details: Record<string, unknown>, fallback: string): string {
  const from = stringDetail(details, 'from');
  const to = stringDetail(details, 'to');
  if (from && to) return `${from} -> ${to}`;
  if (to) return to;
  if (from) return `from ${from}`;
  return fallback;
}

export function formatArtifactActivity(row: ArtifactActivity): FormattedActivity {
  const details = row.details;

  switch (row.eventType) {
    case 'artifact.create':
      return { icon: 'circle', label: 'Created' };
    case 'artifact.metadata_updated':
      return { icon: 'edit', label: 'Metadata updated' };
    case 'artifact.status_changed':
      return {
        icon: 'edit',
        label: 'Status changed',
        detailText: transition(details, 'status changed'),
      };
    case 'artifact.archived':
      return {
        icon: 'archive',
        label: 'Archived',
        detailText: stringDetail(details, 'from')
          ? `from ${stringDetail(details, 'from') ?? ''}`
          : undefined,
      };
    case 'artifact.unarchived':
      return {
        icon: 'archive',
        label: 'Unarchived',
        detailText: stringDetail(details, 'to')
          ? `restored to ${stringDetail(details, 'to') ?? ''}`
          : undefined,
      };
    case 'artifact.superseded':
      return {
        icon: 'shield',
        label: 'Superseded by newer ADR',
        detailText: stringDetail(details, 'superseded_by')
          ? `superseded by ${stringDetail(details, 'superseded_by') ?? ''}`
          : undefined,
      };
    case 'artifact.snapshot_created':
      return {
        icon: 'check',
        label: 'Snapshot created',
        detailText: stringDetail(details, 'name')
          ? `name: ${stringDetail(details, 'name') ?? ''}`
          : (stringDetail(details, 'snapshot_id') ?? undefined),
      };
    case 'artifact.snapshot_approved':
      return {
        icon: 'shield',
        label: 'Snapshot approved',
        detailText: stringDetail(details, 'snapshot_id')
          ? `snapshot: ${stringDetail(details, 'snapshot_id') ?? ''}`
          : undefined,
      };
    case 'artifact.snapshot_revoked':
      return {
        icon: 'shield',
        label: 'Snapshot approval revoked',
        detailText: stringDetail(details, 'reason')
          ? `reason: ${stringDetail(details, 'reason') ?? ''}`
          : undefined,
      };
    case 'artifact.link_created':
      return {
        icon: 'link',
        label: 'Link created',
        detailText: stringDetail(details, 'link_type')
          ? `type: ${stringDetail(details, 'link_type') ?? ''}`
          : undefined,
      };
    case 'artifact.link_deleted':
      return {
        icon: 'link',
        label: 'Link deleted',
        detailText: stringDetail(details, 'link_type')
          ? `type: ${stringDetail(details, 'link_type') ?? ''}`
          : undefined,
      };
    case 'artifact.external_ref_created':
      return {
        icon: 'link',
        label: 'External ref created',
        detailText: stringDetail(details, 'provider')
          ? `provider: ${stringDetail(details, 'provider') ?? ''}${
              stringDetail(details, 'external_id')
                ? ` ${stringDetail(details, 'external_id') ?? ''}`
                : ''
            }`
          : undefined,
      };
    case 'artifact.external_ref_deleted':
      return {
        icon: 'link',
        label: 'External ref deleted',
        detailText: stringDetail(details, 'provider')
          ? `provider: ${stringDetail(details, 'provider') ?? ''}${
              stringDetail(details, 'external_id')
                ? ` ${stringDetail(details, 'external_id') ?? ''}`
                : ''
            }`
          : undefined,
      };
    case 'artifact.visibility_changed':
      return {
        icon: 'shield',
        label: 'Visibility changed',
        detailText: transition(details, 'visibility changed'),
      };
    case 'artifact.owner_transferred':
      return {
        icon: 'user',
        label: 'Owner transferred',
        detailText: transition(details, 'owner transferred'),
      };
    case 'artifact.deleted':
      return { icon: 'trash', label: 'Deleted' };
    case 'artifact.body_updated':
      return {
        icon: 'edit',
        label: 'Body updated',
        detailText:
          details.demoted_from_approved === true ? 'demoted approved artifact to draft' : undefined,
      };
    default:
      return { icon: 'circle', label: row.eventType };
  }
}
